import * as React from "react";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { Box, IconButton, Typography } from "@mui/material";
import { CheckRounded, KeyboardArrowDownRounded } from "@mui/icons-material";
import useUser from "../../../../providers/user/user_provider";
import { blue, green, orange, purple, purpleLight } from "../../../../theme/colors";

export default function StatusMenu({
  status,
  updatedStatus,
  onStatusChange,
}: {
  status: string;
  updatedStatus: string | null;
  onStatusChange: (status: string) => void;
}) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { hasPermission } = useUser();
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  function isStatusClosed() {
    return status === "Closed";
  }

  function isStatusInProgress() {
    return status === "In Progress";
  }

  function isStatusResolved() {
    return status === "Resolved";
  }

  return (
    <div>
      <Typography variant="body2" color="primary.main" fontWeight={600}>
        Status
      </Typography>

      {isStatusClosed() ? (
        <Typography color={green} fontWeight={700}>
          Closed
        </Typography>
      ) : (
        <Button
          sx={{
            color: "text.primary",
            textTransform: "capitalize",
            p: 0,
            m: 0,
            minWidth: 0,
          }}
          id="basic-button"
          aria-controls={open ? "status-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          onClick={handleClick}
          disabled={isStatusClosed() || !hasPermission("needs", "update")}
        >
          <Box display="flex" alignItems="center">
            <Typography
              variant="body1"
              fontWeight={700}
              color={
                !isStatusClosed()
                  ? isStatusInProgress()
                    ? orange
                    : isStatusResolved()
                      ? blue
                      : "text.primary"
                  : green
              }
            >
              {updatedStatus || status}
            </Typography>
            {isStatusClosed() ? null : (
              <IconButton size="small">
                <KeyboardArrowDownRounded sx={{ color: purple }} />
              </IconButton>
            )}
          </Box>
        </Button>
      )}
      <Menu
        id="status-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 0.5,
            borderRadius: 2,
            border: "1px solid #ECECEC",
            boxShadow: "0px 12px 28px -8px rgba(27,0,27,0.18)",
            minWidth: 180,
          },
        }}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
      >
        <MenuItem
          disabled
          selected={status === "Submitted"}
          onClick={() => {
            onStatusChange("Submitted");
            handleClose();
          }}
          sx={{
            "&.Mui-selected": { bgcolor: purpleLight },
            display: "flex",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          Submitted
          {status === "Submitted" && <CheckRounded sx={{ color: purple, fontSize: 18 }} />}
        </MenuItem>
        <MenuItem
          selected={status === "In Progress"}
          disabled={status === "In Progress" || status === "Closed"}
          onClick={() => {
            onStatusChange("In Progress");
            handleClose();
          }}
          sx={{
            "&.Mui-selected": { bgcolor: purpleLight },
            display: "flex",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          In Progress
          {status === "In Progress" && <CheckRounded sx={{ color: purple, fontSize: 18 }} />}
        </MenuItem>
        <MenuItem
          selected={status === "Resolved"}
          disabled={status === "Resolved" || status === "Closed"}
          onClick={() => {
            onStatusChange("Resolved");
            handleClose();
          }}
          sx={{
            "&.Mui-selected": { bgcolor: purpleLight },
            display: "flex",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          Resolved
          {status === "Resolved" && <CheckRounded sx={{ color: purple, fontSize: 18 }} />}
        </MenuItem>
        <MenuItem
          selected={status === "Closed"}
          disabled={status === "Closed"}
          onClick={() => {
            onStatusChange("Closed");
            handleClose();
          }}
          sx={{
            "&.Mui-selected": { bgcolor: purpleLight },
            display: "flex",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          Closed
          {status === "Closed" && <CheckRounded sx={{ color: purple, fontSize: 18 }} />}
        </MenuItem>
      </Menu>
    </div>
  );
}
